import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import MissionVision from "@/pages/MissionVision";
import Leadership from "@/pages/Leadership";
import OurWork from "@/pages/OurWork";
import InitiativeDetail from "@/pages/InitiativeDetail";
import PilArchive from "@/pages/PilArchive";
import PilDetail from "@/pages/PilDetail";
import Media from "@/pages/Media";
import MediaClips from "@/pages/MediaClips";
import PressDetail from "@/pages/PressDetail";
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
import OfficialDocuments from "@/pages/OfficialDocuments";
import OfficialDocumentDetail from "@/pages/OfficialDocumentDetail";
import VerifyDocument from "@/pages/VerifyDocument";
import Contact from "@/pages/Contact";
import Donate from "@/pages/Donate";
import VerifyDonation from "@/pages/VerifyDonation";
import DonorReceipts from "@/pages/DonorReceipts";
import { Privacy, Terms, Refund, Disclaimer } from "@/pages/StaticPages";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import { ForgotPassword, ResetPassword } from "@/pages/admin/AuthPages";
import AdminLayout from "@/components/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import PilList from "@/pages/admin/PilList";
import PilEditor from "@/pages/admin/PilEditor";
import PressList from "@/pages/admin/PressList";
import PressEditor from "@/pages/admin/PressEditor";
import InitiativesAdmin from "@/pages/admin/InitiativesAdmin";
import AreasAdmin from "@/pages/admin/AreasAdmin";
import CoverageAdmin from "@/pages/admin/CoverageAdmin";
import MediaLogosAdmin from "@/pages/admin/MediaLogosAdmin";
import MediaClipsAdmin from "@/pages/admin/MediaClipsAdmin";
import LeadershipAdmin from "@/pages/admin/LeadershipAdmin";
import PagesAdmin from "@/pages/admin/PagesAdmin";
import DocumentsAdmin from "@/pages/admin/DocumentsAdmin";
import OfficialDocsAdmin from "@/pages/admin/OfficialDocsAdmin";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Enquiries from "@/pages/admin/Enquiries";
import DonationsAdmin from "@/pages/admin/DonationsAdmin";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import SeoAdmin from "@/pages/admin/SeoAdmin";
import AuditLogs from "@/pages/admin/AuditLogs";
import TypographyAdmin from "@/pages/admin/TypographyAdmin";
import IssuesAdmin from "@/pages/admin/IssuesAdmin";
import Issues from "@/pages/Issues";
import api from "@/lib/apiClient";
import { applyTypography } from "@/lib/typography";

function TypographyLoader() {
  useEffect(() => { api.get("/settings/typography").then(({ data }) => applyTypography(data)).catch(() => {}); }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let tries = 0;
    const id = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++tries > 20) { clearInterval(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }
    }, 150);
    return () => clearInterval(id);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <TypographyLoader />
      <AuthProvider>
        <SettingsProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/mission-vision" element={<MissionVision />} />
              <Route path="/leadership" element={<Leadership />} />
              <Route path="/our-work" element={<OurWork />} />
              <Route path="/initiatives/:slug" element={<InitiativeDetail />} />
              <Route path="/pil" element={<PilArchive />} />
              <Route path="/pil/:code" element={<PilDetail />} />
              <Route path="/media" element={<Media />} />
              <Route path="/media/clips" element={<MediaClips />} />
              <Route path="/media/:slug" element={<PressDetail />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/documents/:slug" element={<DocumentDetail />} />
              <Route path="/official-documents" element={<OfficialDocuments />} />
              <Route path="/official-documents/:slug" element={<OfficialDocumentDetail />} />
              <Route path="/verify" element={<VerifyDocument />} />
              <Route path="/verify/:docId" element={<VerifyDocument />} />
              <Route path="/issues" element={<Issues />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/verify-donation/:token" element={<VerifyDonation />} />
              <Route path="/verify-donation" element={<VerifyDonation />} />
              <Route path="/receipts" element={<DonorReceipts />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund-policy" element={<Refund />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="pils" element={<PilList />} />
              <Route path="pils/:id" element={<PilEditor />} />
              <Route path="press" element={<PressList />} />
              <Route path="press/:id" element={<PressEditor />} />
              <Route path="areas" element={<AreasAdmin />} />
              <Route path="coverage" element={<CoverageAdmin />} />
              <Route path="media-logos" element={<MediaLogosAdmin />} />
              <Route path="media-clips" element={<MediaClipsAdmin />} />
              <Route path="initiatives" element={<InitiativesAdmin />} />
              <Route path="leadership" element={<LeadershipAdmin />} />
              <Route path="pages" element={<PagesAdmin />} />
              <Route path="documents" element={<DocumentsAdmin />} />
              <Route path="official-documents" element={<OfficialDocsAdmin />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="enquiries" element={<Enquiries />} />
              <Route path="donations" element={<DonationsAdmin />} />
              <Route path="users" element={<UsersAdmin />} />
              <Route path="settings" element={<SettingsAdmin />} />
              <Route path="seo" element={<SeoAdmin />} />
              <Route path="typography" element={<TypographyAdmin />} />
              <Route path="issues" element={<IssuesAdmin />} />
              <Route path="audit" element={<AuditLogs />} />
            </Route>
          </Routes>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
